import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef } from 'react'

import {
    animate,
    motion,
    useInView,
    useMotionValue,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { EASE_OUT_EXPO } from '@/constants/motion'

interface CountUpProps {
    value: number
    className?: string
    duration?: number
}

const countDecimals = (value: number): number => {
    if (Number.isInteger(value)) return 0

    const [, decimals = ''] = String(value).split('.')

    return Math.min(decimals.length, 2)
}

// Renders the final number on the server, then counts up from zero the first
// time it scrolls into view.
export const CountUp: FC<CountUpProps> = ({ value, className, duration = 1.8 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const started = useRef(false)
    const target = Number.isFinite(value) ? value : 0
    const decimals = countDecimals(target)
    const reduced = usePrefersReducedMotion()
    const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })

    const formatter = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }), [decimals])

    const count = useMotionValue(target)
    const display = useTransform(count, latest => formatter.format(latest))

    useEffect(() => {
        if (!started.current) count.set(0)
    }, [count])

    useEffect(() => {
        if (!inView) return

        started.current = true

        if (reduced) {
            count.set(target)
            return
        }

        const controls = animate(count, target, { duration, ease: EASE_OUT_EXPO })

        return () => controls.stop()
    }, [inView, reduced, target, duration, count])

    return (
        <motion.span ref={ref} className={cn('tabular-nums', className)}>
            {display}
        </motion.span>
    )
}
